/**
 * Enhanced Analytics Service
 * Provides comprehensive user behavior tracking and analytics
 * Supports multiple analytics providers (PostHog, Mixpanel, GA4)
 */

import { supabase } from './supabase';

// PostHog configuration
let posthog = null;
const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

// Mixpanel configuration (disabled - install mixpanel-browser if needed)
let mixpanel = null;
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

// Google Analytics 4 configuration
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

/**
 * Initialize analytics providers
 */
export const initializeAnalytics = async () => {
  try {
    // Initialize PostHog
    if (POSTHOG_API_KEY) {
      console.log('🔄 Initializing PostHog with API key:', POSTHOG_API_KEY.substring(0, 8) + '...');
      const { default: posthogLib } = await import('posthog-js');
      
      posthogLib.init(POSTHOG_API_KEY, {
        api_host: POSTHOG_HOST,
        capture_pageview: true, // Enable automatic page view tracking
        capture_pageleave: true,
        session_recording: {
          enabled: true,
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
            email: false
          }
        },
        autocapture: {
          dom_event_allowlist: ['click', 'change', 'submit']
        },
        debug: import.meta.env.DEV, // Enable debug mode in development
        loaded: function(posthog_instance) {
          console.log('✅ PostHog fully loaded and ready');
          // Manually capture initial page view
          posthog_instance.capture('$pageview', {
            $current_url: window.location.href,
            $pathname: window.location.pathname,
            $title: document.title
          });
        }
      });
      
      posthog = posthogLib;
      console.log('✅ PostHog initialized with config:', {
        api_host: POSTHOG_HOST,
        capture_pageview: true,
        debug: import.meta.env.DEV
      });
    } else {
      console.warn('⚠️ PostHog API key not found in environment variables');
    }

    // Mixpanel initialization disabled (install mixpanel-browser package to enable)
    if (MIXPANEL_TOKEN) {
      console.warn('⚠️ Mixpanel token found but package not installed. Run: npm install mixpanel-browser');
    }

    // Initialize Google Analytics 4
    if (GA4_MEASUREMENT_ID) {
      // Load gtag script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      function gtag() { dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', GA4_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href
      });
      console.log('✅ Google Analytics 4 initialized');
    }

  } catch (error) {
    console.error('Analytics initialization error:', error);
  }
};

/**
 * Identify user across all analytics platforms
 */
export const identifyUser = (userId, userProperties = {}) => {
  try {
    const properties = {
      ...userProperties,
      platform: 'web',
      timestamp: new Date().toISOString()
    };

    // PostHog
    if (posthog) {
      posthog.identify(userId, properties);
    }

    // Mixpanel
    if (mixpanel) {
      mixpanel.identify(userId);
      mixpanel.people.set(properties);
    }

    // Google Analytics 4
    if (window.gtag) {
      window.gtag('config', GA4_MEASUREMENT_ID, {
        user_id: userId,
        custom_map: properties
      });
    }

    console.log('User identified:', userId);
  } catch (error) {
    console.error('User identification error:', error);
  }
};

/**
 * Track page views
 */
export const trackPageView = (pagePath, pageTitle = document.title) => {
  try {
    const properties = {
      page_path: pagePath,
      page_title: pageTitle,
      page_url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString()
    };

    // PostHog
    if (posthog) {
      posthog.capture('$pageview', properties);
    }

    // Mixpanel
    if (mixpanel) {
      mixpanel.track('Page View', properties);
    }

    // Google Analytics 4
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: pagePath
      });
    }

    console.log('Page view tracked:', pagePath);
  } catch (error) {
    console.error('Page view tracking error:', error);
  }
};

/**
 * Track custom events
 */
export const trackEvent = (eventName, properties = {}) => {
  try {
    const eventProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_url: window.location.href
    };

    // PostHog
    if (posthog) {
      posthog.capture(eventName, eventProperties);
    }

    // Mixpanel
    if (mixpanel) {
      mixpanel.track(eventName, eventProperties);
    }

    // Google Analytics 4
    if (window.gtag) {
      window.gtag('event', eventName.toLowerCase().replace(/\s+/g, '_'), eventProperties);
    }

    console.log('Event tracked:', eventName, eventProperties);
  } catch (error) {
    console.error('Event tracking error:', error);
  }
};

/**
 * Gaming-specific event tracking
 */
export const trackGameEvent = (action, gameData = {}) => {
  const eventName = `Game ${action}`;
  const properties = {
    game_id: gameData.id,
    game_title: gameData.title,
    game_genre: gameData.genre,
    game_tier: gameData.tier,
    studio: gameData.studio,
    action: action.toLowerCase(),
    ...gameData
  };

  trackEvent(eventName, properties);
};

/**
 * Subscription event tracking
 */
export const trackSubscriptionEvent = (action, subscriptionData = {}) => {
  const eventName = `Subscription ${action}`;
  const properties = {
    subscription_tier: subscriptionData.tier,
    subscription_price: subscriptionData.price,
    subscription_period: subscriptionData.period,
    action: action.toLowerCase(),
    ...subscriptionData
  };

  trackEvent(eventName, properties);
};

/**
 * User engagement tracking
 */
export const trackUserEngagement = (engagementType, data = {}) => {
  const eventName = `User ${engagementType}`;
  const properties = {
    engagement_type: engagementType.toLowerCase(),
    session_duration: data.sessionDuration,
    pages_viewed: data.pagesViewed,
    games_played: data.gamesPlayed,
    ...data
  };

  trackEvent(eventName, properties);
};

/**
 * Performance tracking
 */
export const trackPerformance = (metric, value, context = {}) => {
  const eventName = 'Performance Metric';
  const properties = {
    metric_name: metric,
    metric_value: value,
    page_path: window.location.pathname,
    user_agent: navigator.userAgent,
    connection_type: navigator.connection?.effectiveType || 'unknown',
    ...context
  };

  trackEvent(eventName, properties);
};

/**
 * Error tracking
 */
export const trackError = (error, context = {}) => {
  const eventName = 'Error Occurred';
  const properties = {
    error_message: error.message,
    error_stack: error.stack,
    error_name: error.name,
    page_path: window.location.pathname,
    user_agent: navigator.userAgent,
    ...context
  };

  trackEvent(eventName, properties);
};

/**
 * Revenue tracking
 */
export const trackRevenue = (amount, currency = 'USD', transactionData = {}) => {
  const eventName = 'Revenue Generated';
  const properties = {
    revenue_amount: amount,
    currency: currency,
    transaction_id: transactionData.transactionId,
    payment_method: transactionData.paymentMethod,
    subscription_tier: transactionData.subscriptionTier,
    ...transactionData
  };

  // PostHog
  if (posthog) {
    posthog.capture(eventName, properties);
  }

  // Mixpanel
  if (mixpanel) {
    mixpanel.track(eventName, properties);
    mixpanel.people.track_charge(amount, properties);
  }

  // Google Analytics 4 - Enhanced Ecommerce
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionData.transactionId,
      value: amount,
      currency: currency,
      items: [{
        item_id: transactionData.subscriptionTier,
        item_name: `${transactionData.subscriptionTier} Subscription`,
        category: 'Subscription',
        quantity: 1,
        price: amount
      }]
    });
  }

  console.log('Revenue tracked:', amount, currency);
};

/**
 * A/B Test tracking
 */
export const trackABTest = (testName, variant, properties = {}) => {
  const eventName = 'AB Test Viewed';
  const testProperties = {
    test_name: testName,
    test_variant: variant,
    ...properties
  };

  trackEvent(eventName, testProperties);

  // Set user properties for segmentation
  if (posthog) {
    posthog.people.set({ [`ab_test_${testName}`]: variant });
  }

  if (mixpanel) {
    mixpanel.people.set({ [`ab_test_${testName}`]: variant });
  }
};

/**
 * Get analytics dashboard data
 */
export const getAnalyticsDashboardData = async () => {
  try {
    const data = {
      providers: {
        posthog: !!posthog,
        mixpanel: !!mixpanel,
        ga4: !!window.gtag
      },
      session: {
        sessionId: posthog?.get_session_id?.() || 'unknown',
        distinctId: posthog?.get_distinct_id?.() || 'unknown'
      },
      config: {
        posthog_enabled: !!POSTHOG_API_KEY,
        mixpanel_enabled: !!MIXPANEL_TOKEN,
        ga4_enabled: !!GA4_MEASUREMENT_ID
      }
    };

    return data;
  } catch (error) {
    console.error('Error getting analytics dashboard data:', error);
    return null;
  }
};

/**
 * Reset user data (for logout)
 */
export const resetUser = () => {
  try {
    // PostHog
    if (posthog) {
      posthog.reset();
    }

    // Mixpanel
    if (mixpanel) {
      mixpanel.reset();
    }

    console.log('User data reset');
  } catch (error) {
    console.error('User reset error:', error);
  }
};

/**
 * Check analytics configuration
 */
export const checkAnalyticsConfig = () => {
  return {
    posthog: {
      enabled: !!POSTHOG_API_KEY,
      initialized: !!posthog,
      api_key: POSTHOG_API_KEY ? '***configured***' : 'missing'
    },
    mixpanel: {
      enabled: !!MIXPANEL_TOKEN,
      initialized: !!mixpanel,
      token: MIXPANEL_TOKEN ? '***configured***' : 'missing'
    },
    ga4: {
      enabled: !!GA4_MEASUREMENT_ID,
      initialized: !!window.gtag,
      measurement_id: GA4_MEASUREMENT_ID ? '***configured***' : 'missing'
    }
  };
};

/**
 * Player Journey Tracking for Studio Intelligence Hub
 * Tracks player lifecycle stages: discovered → interested → first_play → return_player → engaged → champion
 */

/**
 * Track player journey stage
 * @param {string} userId - User ID
 * @param {string} gameId - Game ID
 * @param {string} stage - Journey stage: 'discovered', 'interested', 'first_play', 'return_player', 'engaged', 'champion'
 * @param {string} source - Discovery source: 'search', 'featured', 'browse', 'recommendation', 'social', 'direct', 'unknown'
 * @param {object} metadata - Additional metadata
 */
export const trackPlayerJourney = async (userId, gameId, stage, source = null, metadata = {}) => {
  try {
    // Track in Supabase for Studio Intelligence Hub
    const { error } = await supabase
      .from('player_journey_events')
      .insert({
        user_id: userId,
        game_id: gameId,
        journey_stage: stage,
        discovery_source: source,
        metadata: metadata
      });

    if (error) {
      console.error('Journey tracking error:', error);
    }

    // Also track in PostHog for additional analytics
    const eventName = `Player Journey: ${stage}`;
    trackEvent(eventName, {
      user_id: userId,
      game_id: gameId,
      journey_stage: stage,
      discovery_source: source,
      ...metadata
    });

    console.log(`Journey tracked: ${stage} for game ${gameId}`);
  } catch (error) {
    console.error('Player journey tracking error:', error);
  }
};

/**
 * Track game discovery - when player views game page
 */
export const trackGameDiscovered = async (userId, gameId, source = 'direct') => {
  await trackPlayerJourney(userId, gameId, 'discovered', source);
};

/**
 * Track player interest - when player clicks play button or favorites game
 */
export const trackGameInterest = async (userId, gameId, action = 'play_clicked') => {
  await trackPlayerJourney(userId, gameId, 'interested', null, { action });
};

/**
 * Track first play - when player starts first session
 */
export const trackFirstPlay = async (userId, gameId) => {
  // Check if this is truly the first play
  const { data: existingPlays, error } = await supabase
    .from('player_journey_events')
    .select('id')
    .eq('user_id', userId)
    .eq('game_id', gameId)
    .eq('journey_stage', 'first_play')
    .limit(1);

  if (error || (existingPlays && existingPlays.length > 0)) {
    return; // Already tracked or error
  }

  await trackPlayerJourney(userId, gameId, 'first_play');
};

/**
 * Check and track return player - when player returns after 24h
 */
export const checkAndTrackReturnPlayer = async (userId, gameId) => {
  try {
    // Get first play timestamp
    const { data: firstPlay, error: firstPlayError } = await supabase
      .from('player_journey_events')
      .select('created_at')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .eq('journey_stage', 'first_play')
      .order('created_at', { ascending: true })
      .limit(1)
      .single();

    if (firstPlayError || !firstPlay) {
      return; // No first play recorded yet
    }

    // Check if already tracked as return player
    const { data: returnPlayer, error: returnError } = await supabase
      .from('player_journey_events')
      .select('id')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .eq('journey_stage', 'return_player')
      .limit(1);

    if (returnError || (returnPlayer && returnPlayer.length > 0)) {
      return; // Already tracked
    }

    // Check if 24h have passed
    const firstPlayTime = new Date(firstPlay.created_at);
    const now = new Date();
    const hoursDiff = (now - firstPlayTime) / (1000 * 60 * 60);

    if (hoursDiff >= 24) {
      await trackPlayerJourney(userId, gameId, 'return_player', null, {
        hours_since_first_play: Math.round(hoursDiff)
      });
    }
  } catch (error) {
    console.error('Return player check error:', error);
  }
};

/**
 * Check and track engaged player - 5+ sessions or 60+ minutes total playtime
 */
export const checkAndTrackEngaged = async (userId, gameId) => {
  try {
    // Check if already tracked
    const { data: existing, error: existingError } = await supabase
      .from('player_journey_events')
      .select('id')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .eq('journey_stage', 'engaged')
      .limit(1);

    if (existingError || (existing && existing.length > 0)) {
      return; // Already tracked
    }

    // Get session count and total playtime
    const { data: sessions, error: sessionsError } = await supabase
      .from('user_game_sessions')
      .select('duration_minutes')
      .eq('user_id', userId)
      .eq('game_id', gameId);

    if (sessionsError || !sessions) {
      return;
    }

    const sessionCount = sessions.length;
    const totalMinutes = sessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);

    // Track if criteria met: 5+ sessions OR 60+ minutes
    if (sessionCount >= 5 || totalMinutes >= 60) {
      await trackPlayerJourney(userId, gameId, 'engaged', null, {
        session_count: sessionCount,
        total_minutes: totalMinutes
      });
    }
  } catch (error) {
    console.error('Engaged player check error:', error);
  }
};

/**
 * Check and track champion player - favorited + 10+ sessions
 */
export const checkAndTrackChampion = async (userId, gameId) => {
  try {
    // Check if already tracked
    const { data: existing, error: existingError } = await supabase
      .from('player_journey_events')
      .select('id')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .eq('journey_stage', 'champion')
      .limit(1);

    if (existingError || (existing && existing.length > 0)) {
      return; // Already tracked
    }

    // Check if favorited
    const { data: favorite, error: favoriteError } = await supabase
      .from('favorite_games')
      .select('id')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .limit(1);

    if (favoriteError || !favorite || favorite.length === 0) {
      return; // Not favorited
    }

    // Get session count
    const { data: sessions, error: sessionsError } = await supabase
      .from('user_game_sessions')
      .select('id')
      .eq('user_id', userId)
      .eq('game_id', gameId);

    if (sessionsError || !sessions) {
      return;
    }

    const sessionCount = sessions.length;

    // Track if criteria met: favorited AND 10+ sessions
    if (sessionCount >= 10) {
      await trackPlayerJourney(userId, gameId, 'champion', null, {
        session_count: sessionCount,
        is_favorited: true
      });
    }
  } catch (error) {
    console.error('Champion player check error:', error);
  }
};

/**
 * Manual test function for debugging PostHog
 */
export const testPostHogTracking = () => {
  if (!posthog) {
    console.error('❌ PostHog not initialized');
    return false;
  }

  try {
    console.log('🧪 Testing PostHog tracking...');

    // Test basic event
    posthog.capture('test_event', {
      test_property: 'test_value',
      timestamp: new Date().toISOString()
    });

    // Test page view
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      $pathname: window.location.pathname,
      $title: document.title
    });

    console.log('✅ PostHog test events sent');
    console.log('PostHog instance:', posthog);
    console.log('Session ID:', posthog.get_session_id());
    console.log('Distinct ID:', posthog.get_distinct_id());

    return true;
  } catch (error) {
    console.error('❌ PostHog test failed:', error);
    return false;
  }
};

// Make test function available globally for debugging
if (typeof window !== 'undefined') {
  window.testPostHogTracking = testPostHogTracking;
}

// Export default analytics functions for easy use
export default {
  initialize: initializeAnalytics,
  identify: identifyUser,
  trackPageView,
  trackEvent,
  trackGameEvent,
  trackSubscriptionEvent,
  trackUserEngagement,
  trackPerformance,
  trackError,
  trackRevenue,
  trackABTest,
  getDashboardData: getAnalyticsDashboardData,
  reset: resetUser,
  checkConfig: checkAnalyticsConfig,
  testTracking: testPostHogTracking,
  // Player Journey Tracking
  trackPlayerJourney,
  trackGameDiscovered,
  trackGameInterest,
  trackFirstPlay,
  checkAndTrackReturnPlayer,
  checkAndTrackEngaged,
  checkAndTrackChampion
};
